<template>
    <div>
        <PageHeader :title="pageHeader">
            <template v-slot:icon>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="page-header-icon">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M17.982 18.725A7.488 7.488 0 0 0 12 15.75a7.488 7.488 0 0 0-5.982 2.975m11.963 0a9 9 0 1 0-11.963 0m11.963 0A8.966 8.966 0 0 1 12 21a8.966 8.966 0 0 1-5.982-2.275M15 9.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                </svg>
            </template>
        </PageHeader>

        <div class="p-2 pb-4 bg-white rounded-md" v-if="!loadingEntireScreen">
            <div class="flex justify-center items-center mb-4">
                <img class="w-52" src="../assets/logo-megaluzz.png" alt="Megaluzz Logo">
            </div>

            <div class="flex w-full gap-x-4 justify-center">
                <div class="relative w-8/12">
                    <span class="absolute inset-y-0 left-3 flex items-center text-gray-500">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                            <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
                        </svg>
                    </span>
                    <input
                        placeholder="Pesquisar por nome do vendedor..."
                        class="w-full pl-10 uppercase pr-4 text-center p-2 bg-white border border-gray-300 font-bold text-gray-500 text-base rounded-md focus:outline-none focus:ring focus:ring-opacity-25 focus:ring-green-400 disabled:bg-gray-100"
                        v-model="filter.name"
                        @keyup.enter="loadData"
                    />
                </div>

                <div>
                    <BaseSelect
                        class="w-full h-11 rounded-md text-base"
                        :options="typeOptions"
                        v-model="filter.type"
                        emptyOption="Todos"
                    />
                </div>
            </div>

            <div class="flex justify-center gap-6 my-4">
                <div class="flex">
                    <div class="bg-green-50 text-green-700 px-4 py-2 rounded-lg font-bold text-sm shadow-sm">
                        Embaixadores: {{ filteredSellers.filter(s => s.type === 'ambassador').length }}
                    </div>
                    <div class="bg-green-50 text-green-700 px-4 py-2 rounded-lg font-bold text-sm shadow-sm">
                        Representantes: {{ filteredSellers.filter(s => s.type === 'representative').length }}
                    </div>
                    <div class="bg-green-50 text-green-700 px-4 py-2 rounded-lg font-bold text-sm shadow-sm">
                        Promotores: {{ filteredSellers.filter(s => s.type === 'promoter').length }}
                    </div>
                </div>
            </div>

            <BaseButton @click="setModalStatus('open')" skin="primary-small" class="mb-4 ml-4 mt-4 w-32">
                <template v-slot:icon>
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                </template>
                Adicionar
            </BaseButton>
        </div>

        <Alert text="Ainda não existem vendedores cadastrados" :show="!sellers.length && !loading" />

        <div class="table-container" v-if="sellers.length && !loadingEntireScreen">

            <table class="w-full" v-if="!loading">
                <thead>
                    <tr>
                        <th class="text-center">#</th>
                        <th>NOME</th>
                        <th>EMAIL (LOGIN)</th>
                        <th>WHATSAPP</th>
                        <th>UF/CIDADE</th>
                        <th>Ações</th>
                    </tr>
                </thead>
                <tbody>
                    <tr class="table-row" v-for="(seller, index) in filteredSellers" :key="seller.id">
                        <td class="text-center font-black text-green-500">#{{ index + 1 }}</td>
                        <td>
                            <div style="width: 350px" class="font-semibold break-all whitespace-normal text-sm text-green-700 uppercase">
                                <div class="flex flex-col text-center">
                                    <span class="bg-green-50 p-1 text-xs font-bold rounded-lg" v-if="seller.type === 'ambassador'">(Embaixador)</span>
                                    <span class="bg-green-50 p-1 text-xs font-bold rounded-lg" v-if="seller.type === 'representative'">(Representante)</span>
                                    <span class="bg-green-50 p-1 text-xs font-bold rounded-lg" v-if="seller.type === 'promoter'">(Promotor)</span>
                                    <span>
                                        {{ seller.name }}
                                        <a v-if="seller.file_url" class="hover:bg-green-200 float-left w-6 rounded-full mr-2" target="_blank" :href="seller.file_url">
                                            <svg xmlns="http://www.w3.org/2000/svg" v-if="seller.file_url" class="h-6 w-6 rounded-full text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                            </svg>
                                        </a>
                                    </span>
                                </div>
                            </div>
                        </td>
                        <td style="width: 30%">
                            <span class="text-sm text-green-700 break-all whitespace-normal">
                                {{ seller.email }}
                            </span>
                        </td>
                        <td>
                            <div class="flex gap-x-2">
                                <p class="text-sm text-green-700">{{ seller.whatsapp || '-' }}</p>
                                <a
                                    v-if="seller.whatsapp"
                                    :href="`https://wa.me/55${UtilService.removeSpecialCharV2(seller.whatsapp)}`"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-whatsapp w-5 h-5 text-green-500" viewBox="0 0 16 16">
                                        <path d="M13.601 2.326A7.85 7.85 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.9 7.9 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.9 7.9 0 0 0 13.6 2.326zM7.994 14.521a6.6 6.6 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.56 6.56 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592m3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.065-.315-.099-.445.099-.133.197-.513.646-.627.775-.114.133-.232.148-.43.05-.197-.1-.836-.308-1.592-.985-.59-.525-.985-1.175-1.103-1.372-.114-.198-.011-.304.088-.403.087-.088.197-.232.296-.346.1-.114.133-.198.198-.33.065-.134.034-.248-.015-.347-.05-.099-.445-1.076-.612-1.47-.16-.389-.323-.335-.445-.34-.114-.007-.247-.007-.38-.007a.73.73 0 0 0-.529.247c-.182.198-.691.677-.691 1.654s.71 1.916.81 2.049c.098.133 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.904.129 1.246.08.38-.058 1.171-.48 1.338-.943.164-.464.164-.86.114-.943-.049-.084-.182-.133-.38-.232"/>
                                    </svg>
                                </a>
                            </div>
                        </td>
                        <td class="text-sm text-green-700">{{ seller.state_obj?.name }} / {{ seller.city_obj?.name }}</td>
                        <td class="flex gap-x-4">
                            <svg @click="openEditModal(seller)" class="w-6 h-6 text-green-500 cursor-pointer" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
                            </svg>
                            <svg v-if="authInfo.has_delete" @click="deleteSeller(seller)" class="w-6 h-6 text-red-500 cursor-pointer" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                            </svg>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div class="flex justify-center pb-4">
            <Loading :show="loading" text="Carregando dados, aguarde..." />
        </div>

        <ModalV2 ref="modalRef" :hide="true" size="lg" title="Cadastro Vendedor" action-text="Salvar" trigger-text="Adicionar">
            <template v-slot:icon>
                <svg xmlns="http://www.w3.org/2000/svg" class="mr-2 h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clip-rule="evenodd" />
                </svg>
            </template>
            <template v-slot:body>
                <div class="grid grid-cols-4 gap-x-4">
                    <div>
                        <BaseInput label="Nome do Vendedor" :is-invalid="v$.name.$error" @blur="v$.name.$touch" placeholder="Nome do vendedor" v-model="selectedSeller.name" :required="true" />
                        <div class="-mt-2" v-for="error in v$.$errors" :key="error.$uid">
                            <BaseErrorMessage v-if="error.$property === 'name'" :message="error.$message" />
                        </div>
                    </div>
                    <div>
                        <BaseInput label="Email (login)"  :is-invalid="v$.email.$error" @blur="v$.email.$touch" placeholder="vendedor@email.com" v-model="selectedSeller.email" :required="true" />
                        <div class="-mt-2" v-for="error in v$.$errors" :key="error.$uid">
                            <BaseErrorMessage v-if="error.$property === 'email'" :message="error.$message" />
                        </div>
                    </div>
                    <div>
                        <BaseInput label="Email 2" placeholder="vendedor@email.com" v-model="selectedSeller.email2" />
                    </div>
                    <div>
                        <BaseInput label="Telefone" placeholder="" v-model="selectedSeller.phone" v-maska="MaskService['phone']"  />
                    </div>
                </div>
                <div class="grid grid-cols-4 gap-x-4 mt-2">
                    <div>
                        <BaseInput label="Whatsapp" placeholder="" v-model="selectedSeller.whatsapp" v-maska="MaskService['phone']"  />
                    </div>
                    <div>
                        <BaseInput label="CNPJ/CPF" placeholder="" v-model="selectedSeller.cpf" v-maska="MaskService['document']"  />
                    </div>
                    <div class="col-span-2">
                        <BaseInput label="Endereço" placeholder="" v-model="selectedSeller.address" />
                    </div>
                </div>
                <div class="grid grid-cols-4 gap-x-4 mt-2">
                    <div>
                        <BaseInput label="CEP" placeholder="" v-model="selectedSeller.cep" v-maska="MaskService['cep']"  />
                    </div>
                    <div>
                        <BaseSelect label="Estado" :options="stateOptions" v-model.number="selectedSeller.state" @change="loadCities(selectedSeller.state)" />
                    </div>
                    <div>
                        <BaseSelect label="Cidade" :options="citiesOptions" :disabled="!selectedSeller.state || loadingCities" v-model.number="selectedSeller.city" />
                        <span v-if="loadingCities" class="text-xs text-green-500">Carregando cidades...</span>
                    </div>
                    <div>
                        <BaseSelect label="Tipo" :options="typeOptions" v-model.number="selectedSeller.type" />
                    </div>
                </div>
                <div class="grid grid-cols-4 gap-x-4 mt-2">
                    <div class="flex">
                        <div class="flex flex-col">
                            <BaseInput
                                :is-invalid="v$.password.$error"
                                @blur="v$.password.$touch"
                                :type="showPassword ? 'text' : 'password'"
                                label="Senha"
                                placeholder="••••••"
                                v-model="selectedSeller.password"
                                :required="true"
                            />
                            <div class="-mt-1" v-for="error in v$.$errors" :key="error.$uid">
                                <BaseErrorMessage v-if="error.$property === 'password'" :message="error.$message" />
                            </div>
                        </div>
                        <button @click="showPassword = !showPassword" class="px-2 ml-4 w-auto mt-8 bg-green-500 h-8 text-white rounded-md text-center hover:bg-green-700">{{ showPassword ? 'Esconder' : 'Mostrar' }}</button>
                    </div>
                </div>
                <!-- UPLOAD -->
                <div class="grid grid-cols-1 form-row">
                    <div class="p-8 flex gap-x-6">
                        <div class="flex flex-col">
                            <a class="flex gap-x-4 mt-4 hover:bg-green-100 p-2 rounded-md" target="_blank" :href="selectedSeller.file_url">
                                <span class="text-green-500 font-bold">{{ selectedSeller.file_url ? 'ANEXADO' : 'SEM ARQUIVO' }}</span>
                                <svg xmlns="http://www.w3.org/2000/svg" v-if="selectedSeller.file_url" class="h-6 w-6 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                </svg>
                            </a>
                        </div>
                        <FileUploader class="mt-4" text="Enviar Arquivo" :showProgress="true" @onUpload="onUpload" />
                        <svg v-if="selectedSeller.file_url" @click="deleteFile" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6 text-red-600 mt-6 cursor-pointer">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                        </svg>
                    </div>
                </div>
            </template>
            <template v-slot:footer>
                <BaseButton skin="primary-outline" @click="setModalStatus('close')">Fechar</BaseButton>
                <BaseButton skin="primary" :disabled="loadingSubmitSeller" @click="submit()">
                    <svg v-if="loadingSubmitSeller" class="animate-spin ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {{ isEdit ? 'Atualizar' : 'Salvar' }}
                </BaseButton>
            </template>
        </ModalV2>
        
    </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import Alert from '@/components/Alert';
import Loading from '@/components/Loading';
import BaseButton from '@/components/BaseButton';
import PageHeader from '@/components/PageHeader';
import { useVuelidate } from '@vuelidate/core'
import { SellerService, GeoInfoService } from '../services/Entity';
import { 
    requiredMessage,
    minLengthMessage,
} from '../services/VuelidateMessageService';
import ModalV2 from '../components/ModalV2';
import FileUploader from '@/components/FileUploader';
import MaskService from '../services/MaskService';
import Swal from 'sweetalert';
import UtilService from '../services/UtilService';
import AuthService from '../services/AuthService';

export default {
    components: {
        Alert,
        Loading,
        BaseButton,
        PageHeader,
        ModalV2,
        FileUploader
    },
    setup() {
        const pageHeader = ref('Vendedores');
        const filter = ref({
            name: '',
            type: ''
        });
        const loading = ref(false);
        const page = ref(1);
        const limit = ref(50);
        const totalItens = ref(0);
        const loadingSubmitSeller = ref(false);
        const loadingEntireScreen = ref(false);
        const stateOptions = ref([]);
        const citiesOptions = ref([]);
        const modalRef = ref();
        const isEdit = ref(false);
        const loadingCities = ref(false);
        const authInfo = AuthService.getAuthInfo();
        const showPassword = ref(false);
        const sellerRules = computed(() => ({
            name: { required: requiredMessage, minLength: minLengthMessage(2) },
            password: { required: requiredMessage },
            email: { required: requiredMessage }
        }));

        const selectedSeller = ref({
            name: '',
            phone: '',
            whatsapp: '',
            email: '',
            email2: ''

        });
        const sellers = ref([]);
        const typeOptions = ref([
            {
                title: 'Embaixador',
                value: 'ambassador'
            },
            {
                title: 'Representante',
                value: 'representative'
            },
            {
                title: 'Promotor',
                value: 'promoter'
            }
        ]);

        const filteredSellers = computed(() => {
            return sellers.value.filter((seller) => {
                const matchesName = seller.name?.toLowerCase().includes(filter.value.name.toLowerCase())
                const matchesType = !filter.value.type || seller.type === filter.value.type
                return matchesName && matchesType
            })
        });

        onMounted(async () => {
            loading.value = true;
            await loadStates();
            await loadSellers();
            loading.value = false;
        });

        async function submit() {
            const isValid = await v$.value.$validate();
            if (!isValid) return;

            if (!isEdit.value) {
                await save();
            } else {
                await update();
            }
        }

        async function save() {
            const selectedService = { ...selectedSeller.value, country: 1 };

            try {
                loadingSubmitSeller.value = true;
                const { data } = await SellerService.create(selectedService);

                sellers.value.push(data);
                selectedSeller.value = {};
                loadingSubmitSeller.value = false;
                setModalStatus('close');
            } catch(err) {
                loadingSubmitSeller.value = false;
                console.log(err);

                if (err.data?.error == 'already_exists') {
                    Swal({
                        title: 'Já existe um vendedor associado a este email',
                        text: 'O Email deve ser único, use outro email',
                        icon: 'warning'
                    });
                    return;
                }

                Swal({
                    title: 'Ocorreu um erro ao salvar o cliente',
                    text: 'Atualize a página e tente novamente',
                    icon: 'error'
                });
            }
        }

        async function loadSellers(filter) {
            try {
                const listingFilter = { page: 1, limit: 9999 };
                const { data: { total, items } } = await SellerService.list(listingFilter.page, listingFilter.limit, filter);

                for (let seller of items) {
                    seller.state_obj = seller.state;
                    seller.state = seller.state?.id || null;
                    seller.city_obj = seller.city;
                    seller.city = seller.city?.id || null;
                }

                sellers.value = items;
                totalItens.value = total;
            } catch(err) {
                loading.value = false;
                console.log(err);
                Swal({
                    title: 'Ocorreu um erro ao obter dados dos vendedores',
                    text: 'Atualize a página e tente novamente',
                    icon: 'error'
                });
            }
        }

        async function loadStates() {
            try {
                let countryId = 1;
                const { data } = await GeoInfoService.listStates(countryId);

                stateOptions.value = data.map(state => {
                    return {
                        title: state.name,
                        value: state.id
                    }
                });
            } catch(err) {
                loading.value = false;
                console.log(err);
                Swal({
                    title: 'Ocorreu um erro ao listar os estados',
                    text: 'Atualize a página e tente novamente',
                    icon: 'error'
                });
            }
        }

        async function loadCities(stateId) {
            try {
                if (!stateId) {
                    return;
                }

                loadingCities.value = true;
                const { data } = await GeoInfoService.listCities(stateId);

                citiesOptions.value = data.map(city => {
                    return {
                        title: city.name,
                        value: city.id
                    }
                });

                loadingCities.value = false;
            } catch(err) {
                loadingCities.value = false;
                console.log(err);
                Swal({
                    title: 'Ocorreu um erro ao listar as cidades',
                    text: 'Atualize a página e tente novamente',
                    icon: 'error'
                });
            }
        }

        function setModalStatus(status) {
			modalRef.value.setModalStatus(status);
		}

        async function openEditModal(seller) {
            isEdit.value = true;
            selectedSeller.value = { ...seller };
            setModalStatus('open');

            if (selectedSeller.value.state_obj?.id) {
                await loadCities(selectedSeller.value.state_obj.id);
            }

        }

        function onUpload(data) {
            const { success } = data;
            const file = success[0];

            selectedSeller.value.file = file.id;
            selectedSeller.value.file_url = file.url;
        }

        async function deleteFile() {
            Swal({
                title: 'Tem certeza que deseja remover esse arquivo?',
                text: 'Após remover, é preciso clicar em atualizar',
                icon: 'warning',
                buttons: {
                    cancel: 'Cancelar',
                    confirm: {
                        text: 'Remover',
                        className: 'swal-button--danger'
                    }
                },
                dangerMode: true
            }).then(async (confirm) => {
                if (confirm) {
                    selectedSeller.value.file = null;
                    delete selectedSeller.value.file_url;
                }
            });
        }

        async function update() {
            const selectedSellerToUpdate = { ...selectedSeller.value };
            
            try {
                loadingSubmitSeller.value = true;
                const { data } = await SellerService.update(selectedSellerToUpdate);

                for (let [index, seller] of sellers.value.entries()) {
                    if (seller.id === data.id) {
                        sellers.value.splice(index, 1, data);
                    }
                }
                
                selectedSeller.value = {};
                loadingSubmitSeller.value = false;
                setModalStatus('close');
            } catch(err) {
                loadingSubmitSeller.value = false;
                console.log(err);

                if (err.data?.error == 'already_exists') {
                    Swal({
                        title: 'Já existe um vendedor associado a este email',
                        text: 'O Email deve ser único, use outro email',
                        icon: 'warning'
                    });
                    return;
                }

                Swal({
                    title: 'Ocorreu um erro ao atualizar',
                    text: 'Atualize a página e tente novamente',
                    icon: 'error'
                });
            }
        }

        async function deleteSeller(seller) {
            Swal({
                title: 'Deseja mesmo excluir esse vendedor?',
                text: 'Essa ação será registrada, e não pode ser desfeita',
                icon: 'warning',
                buttons: {
                    cancel: 'Cancelar',
                    confirm: {
                        text: 'Excluir Agora',
                        className: 'swal-button--danger'
                    }
                },
                dangerMode: true
            }).then(async (confirm) => {
                if (confirm) {
                    for (let [index, sellerItem] of sellers.value.entries()) {
                        if (sellerItem.id === seller.id) {
                            sellers.value.splice(index, 1);
                        }
                    }
                    
                    const { data } = await SellerService.destroy(seller.id);
                    
                }
            });
        }

        function generateStrongPassword(length = 8) {
            const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
            const lower = 'abcdefghijklmnopqrstuvwxyz'
            const numbers = '0123456789'
            const symbols = '!@#$%^&*()-_=+[]{}<>?'
            const all = upper + lower + numbers + symbols

            let password = ''
            password += upper[Math.floor(Math.random() * upper.length)]
            password += lower[Math.floor(Math.random() * lower.length)]
            password += numbers[Math.floor(Math.random() * numbers.length)]
            password += symbols[Math.floor(Math.random() * symbols.length)]

            for (let i = password.length; i < length; i++) {
                password += all[Math.floor(Math.random() * all.length)]
            }

            // Embaralha a senha para não ficar previsível
            return password
                .split('')
                .sort(() => Math.random() - 0.5)
                .join('')
        }

        const v$ = useVuelidate(sellerRules, selectedSeller);

        return {
            generateStrongPassword,
            showPassword,
            filteredSellers,
            typeOptions,
            deleteSeller,
            authInfo,
            UtilService,
            loadingCities,
            page,
            limit,
            update,
            loadCities,
            deleteFile,
            onUpload,
            submit,
            openEditModal,
            MaskService,
            v$,
            isEdit,
            loadingSubmitSeller,
            modalRef,
            setModalStatus,
            pageHeader,
            filter,
            loading,
            loadingEntireScreen,
            selectedSeller,
            sellers,
            totalItens,
            stateOptions,
            citiesOptions
        };
    }
}
</script>